import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const REGIONAL_METALS = [
  { id: 'acero_inox', name: 'Acero inoxidable', type: 'REGIONAL_INDEX', color: '#64748B', unit: '€/t' },
  { id: 'bronce', name: 'Bronce', type: 'REGIONAL_INDEX', color: '#92400E', unit: '€/t' },
  { id: 'hierro_acero', name: 'Hierro/Acero', type: 'REGIONAL_INDEX', color: '#475569', unit: '€/t' },
  { id: 'laton', name: 'Latón', type: 'REGIONAL_INDEX', color: '#D97706', unit: '€/t' },
];

const LME_METALS = [
  { id: 'cobre', name: 'Cobre', type: 'LME', color: '#EA580C', unit: '$/t' },
  { id: 'aluminio', name: 'Aluminio', type: 'LME', color: '#3B82F6', unit: '$/t' },
  { id: 'zinc', name: 'Zinc', type: 'LME', color: '#059669', unit: '$/t' },
  { id: 'plomo', name: 'Plomo', type: 'LME', color: '#6B7280', unit: '$/t' },
  { id: 'niquel', name: 'Níquel', type: 'LME', color: '#7C3AED', unit: '$/t' },
];

const INITIAL_BENCHMARKS = {
  acero_inox: { value: 1850, override: null, lastUpdated: '2026-03-04T14:30:00Z', enabled: false },
  bronce: { value: 5200, override: null, lastUpdated: '2026-03-04T14:30:00Z', enabled: false },
  hierro_acero: { value: 420, override: null, lastUpdated: '2026-03-04T14:30:00Z', enabled: false },
  laton: { value: 4100, override: null, lastUpdated: '2026-03-04T14:30:00Z', enabled: false },
  cobre: { value: 9250, override: null, lastUpdated: '2026-03-04T16:22:00Z', enabled: false },
  aluminio: { value: 2380, override: null, lastUpdated: '2026-03-04T16:22:00Z', enabled: false },
  zinc: { value: 2640, override: null, lastUpdated: '2026-03-04T16:22:00Z', enabled: false },
  plomo: { value: 1920, override: null, lastUpdated: '2026-03-04T16:22:00Z', enabled: false },
  niquel: { value: 15800, override: null, lastUpdated: '2026-03-04T16:22:00Z', enabled: false },
};

const INITIAL_EURUSD = {
  live: 1.0842,
  override: null,
  enabled: false,
  lastUpdated: '2026-03-04T16:35:00Z',
};

function formatTimestamp(iso) {
  try {
    const d = new Date(iso);
    return d?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function Toggle({ checked, onChange }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 ${
        checked ? 'bg-accent' : 'bg-muted-foreground/30'
      }`}
    >
      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

function BenchmarkRow({ metal, data, onToggle, onOverrideChange, onRefresh }) {
  return (
    <div className="flex items-center gap-3 px-3 py-3 rounded-md bg-muted border border-border mb-2 last:mb-0">
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: metal?.color }} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>
          {metal?.name}
        </p>
        <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.6875rem' }}>
          {metal?.type} · Actualizado: {formatTimestamp(data?.lastUpdated)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="text-foreground"
          style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
        >
          {data?.value?.toLocaleString('es-ES')} {metal?.unit}
        </span>
        <Icon name="ArrowRight" size={12} color="var(--color-muted-foreground)" strokeWidth={2} />
      </div>
      <div className="flex items-center gap-2">
        <Toggle checked={data?.enabled} onChange={onToggle} />
        {data?.enabled && (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={data?.override ?? data?.value}
              onChange={(e) => onOverrideChange(parseFloat(e?.target?.value) || 0)}
              className="w-24 bg-background border border-accent rounded px-2 py-1 text-foreground focus:outline-none"
              style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
              step={0.5}
            />
            <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>{metal?.unit}</span>
          </div>
        )}
        {!data?.enabled && (
          <button
            onClick={onRefresh}
            className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-accent hover:bg-muted transition-colors"
            title="Actualizar valor"
          >
            <Icon name="RefreshCw" size={13} color="currentColor" strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}

function LmeBenchmarkRow({ metal, data }) {
  return (
    <div className="flex items-center gap-3 px-3 py-3 rounded-md bg-muted border border-border mb-2 last:mb-0">
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: metal?.color }} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>
          {metal?.name}
        </p>
        <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.6875rem' }}>
          {metal?.type} · Actualizado: {formatTimestamp(data?.lastUpdated)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="text-foreground font-medium"
          style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
        >
          {data?.value?.toLocaleString('es-ES')} {metal?.unit}
        </span>
      </div>
      <div className="flex items-center gap-1.5 px-2 py-0.5 bg-muted-foreground/10 rounded text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.6875rem' }}>
        <Icon name="Lock" size={10} color="currentColor" strokeWidth={2} />
        <span>Feed automático</span>
      </div>
    </div>
  );
}

export default function MarketFeedSettings() {
  const [eurusd, setEurusd] = useState(INITIAL_EURUSD);
  const [benchmarks, setBenchmarks] = useState(INITIAL_BENCHMARKS);
  const [refreshing, setRefreshing] = useState(false);

  const handleEurUsdToggle = (v) => setEurusd((prev) => ({ ...prev, enabled: v, override: v ? prev?.live : null }));
  const handleEurUsdOverride = (v) => setEurusd((prev) => ({ ...prev, override: v }));

  const handleBenchmarkToggle = (id, v) => {
    setBenchmarks((prev) => ({
      ...prev,
      [id]: { ...prev?.[id], enabled: v, override: v ? prev?.[id]?.value : null },
    }));
  };

  const handleBenchmarkOverride = (id, v) => {
    setBenchmarks((prev) => ({
      ...prev,
      [id]: { ...prev?.[id], override: v },
    }));
  };

  const handleRefreshAll = () => {
    setRefreshing(true);
    setTimeout(() => {
      const now = new Date()?.toISOString();
      setBenchmarks((prev) => {
        const updated = {};
        Object.keys(prev)?.forEach((k) => {
          updated[k] = { ...prev?.[k], lastUpdated: now };
        });
        return updated;
      });
      setEurusd((prev) => ({ ...prev, lastUpdated: now }));
      setRefreshing(false);
    }, 1200);
  };

  const activeOverrides = Object.values(benchmarks)?.filter((b) => b?.enabled)?.length + (eurusd?.enabled ? 1 : 0);

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-foreground mb-1" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.125rem', fontWeight: '600' }}>
            Configuración de Feeds de Mercado
          </h3>
          <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>
            Gestiona overrides manuales para tipos de cambio y benchmarks de referencia.
          </p>
        </div>
        <button
          onClick={handleRefreshAll}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-lg text-foreground hover:bg-border transition-colors disabled:opacity-50"
          style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}
        >
          <Icon name="RefreshCw" size={14} color="currentColor" strokeWidth={2} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Actualizando...' : 'Actualizar todo'}
        </button>
      </div>
      {activeOverrides > 0 && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-warning/10 border border-warning/20 rounded-lg">
          <Icon name="AlertTriangle" size={15} color="var(--color-warning)" strokeWidth={2} />
          <span className="text-warning" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', fontWeight: '500' }}>
            {activeOverrides} override{activeOverrides > 1 ? 's' : ''} manual{activeOverrides > 1 ? 'es' : ''} activo{activeOverrides > 1 ? 's' : ''}. Los valores de mercado en tiempo real están siendo ignorados.
          </span>
        </div>
      )}
      {/* EUR/USD Override */}
      <div className="mb-6 p-5 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="DollarSign" size={18} color="var(--color-accent)" strokeWidth={2} />
          <h4 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>
            Tipo de Cambio EUR/USD
          </h4>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-muted-foreground mb-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>Valor en tiempo real</p>
              <span className="font-bold text-foreground" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums' }}>
                {eurusd?.live?.toFixed(4)}
              </span>
            </div>
            <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
            <div>
              <p className="text-muted-foreground mb-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>Override manual</p>
              <div className="flex items-center gap-2">
                <Toggle checked={eurusd?.enabled} onChange={handleEurUsdToggle} />
                {eurusd?.enabled && (
                  <input
                    type="number"
                    value={eurusd?.override ?? eurusd?.live}
                    onChange={(e) => handleEurUsdOverride(parseFloat(e?.target?.value) || 0)}
                    step={0.0001}
                    min={0.5}
                    max={2}
                    className="w-28 bg-background border border-accent rounded px-2 py-1.5 text-foreground focus:outline-none"
                    style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="ml-auto">
            <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>
              Última actualización: {formatTimestamp(eurusd?.lastUpdated)}
            </p>
          </div>
        </div>
      </div>
      {/* Regional Index Benchmarks */}
      <div className="mb-6 p-5 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="MapPin" size={16} color="var(--color-accent)" strokeWidth={2} />
          <h4 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>
            Índices Regionales
          </h4>
        </div>
        <p className="text-muted-foreground mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>
          Activa el toggle para introducir un valor manual que reemplazará el índice regional.
        </p>
        {REGIONAL_METALS?.map((metal) => (
          <BenchmarkRow
            key={metal?.id}
            metal={metal}
            data={benchmarks?.[metal?.id]}
            onToggle={(v) => handleBenchmarkToggle(metal?.id, v)}
            onOverrideChange={(v) => handleBenchmarkOverride(metal?.id, v)}
            onRefresh={() => {}}
          />
        ))}
      </div>
      {/* LME Benchmarks - read-only, feed only */}
      <div className="p-5 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="TrendingUp" size={16} color="var(--color-accent)" strokeWidth={2} />
          <h4 className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem' }}>
            Benchmarks LME
          </h4>
        </div>
        <p className="text-muted-foreground mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>
          Los valores LME se actualizan exclusivamente mediante feed automático. No se permiten overrides manuales.
        </p>
        {LME_METALS?.map((metal) => (
          <LmeBenchmarkRow
            key={metal?.id}
            metal={metal}
            data={benchmarks?.[metal?.id]}
          />
        ))}
      </div>
    </div>
  );
}
